import type { DefectFeaturesResponseBody } from '../contracts/defectFeatures';
import type { DefectRule } from '../contracts/defectRulesets';
import {
  DEFECT_BAND_SLUGS,
  type DefectBandLegendItem,
  type DefectBandSlug,
} from '../map/buildRulesFromColorScheme';

export const WORST_CONDITION_RULESET_SLUG = 'worst-condition';

export const WORST_CONDITION_BAND_SLUGS: readonly DefectBandSlug[] = [
  'high',
  'very-high',
];

const WORST_BAND_SET = new Set<string>(WORST_CONDITION_BAND_SLUGS);

export function isWorstConditionRuleset(rulesetSlug: string | null | undefined): boolean {
  if (typeof rulesetSlug !== 'string') {
    return false;
  }
  const slug = rulesetSlug.trim().toLowerCase();
  return (
    slug === WORST_CONDITION_RULESET_SLUG ||
    slug.includes('worst-condition') ||
    slug.includes('worst_condition') ||
    (slug.includes('worst') && slug.includes('condition'))
  );
}

export function resolveWorstConditionThreshold(
  thresholds: readonly number[],
): number | null {
  if (thresholds.length === 0) {
    return null;
  }
  const last = thresholds[thresholds.length - 1];
  return typeof last === 'number' && Number.isFinite(last) ? last : null;
}

export function isWorstConditionBand(band: string | null | undefined): boolean {
  if (typeof band !== 'string') {
    return false;
  }
  return WORST_BAND_SET.has(band.trim().toLowerCase());
}

export function isWorstConditionFeature(
  properties: {
    readonly band?: string;
    readonly value?: number;
  } | null | undefined,
  conditionThreshold: number | null,
): boolean {
  if (!properties) {
    return false;
  }

  if (isWorstConditionBand(properties.band)) {
    return true;
  }

  if (
    conditionThreshold != null &&
    typeof properties.value === 'number' &&
    Number.isFinite(properties.value)
  ) {
    return properties.value >= conditionThreshold;
  }

  return false;
}

export function filterFeaturesToWorstCondition(
  collection: DefectFeaturesResponseBody,
  thresholds: readonly number[] = [],
): DefectFeaturesResponseBody {
  const conditionThreshold = resolveWorstConditionThreshold(thresholds);
  return {
    type: 'FeatureCollection',
    features: collection.features.filter((feature) =>
      isWorstConditionFeature(feature.properties, conditionThreshold),
    ),
  };
}

export function filterRulesToWorstCondition(
  rules: readonly DefectRule[],
): readonly DefectRule[] {
  return rules.filter((rule) => isWorstConditionBand(rule.slug));
}

export function filterLegendItemsToWorstCondition(
  items: readonly DefectBandLegendItem[],
): readonly DefectBandLegendItem[] {
  return items.filter((item) => isWorstConditionBand(item.slug));
}

export function worstConditionBandIndexes(): readonly number[] {
  return WORST_CONDITION_BAND_SLUGS.map((slug) => DEFECT_BAND_SLUGS.indexOf(slug)).filter(
    (index) => index >= 0,
  );
}
